import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ShieldCheck, AlertTriangle, Plus, ArrowRight, TrendingUp, FileText, Clock, Globe, RefreshCcw, Zap } from 'lucide-react';
import axios from 'axios';
import ScoreHistoryChart from '../components/ScoreHistoryChart';

const API_URL = import.meta.env?.VITE_API_URL || 'https://cyberguard-api-ka8f.onrender.com';

const PLAN_LIMITS = { free: 1, starter: 5, pro: 30, enterprise: null };

export default function Dashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [stats, setStats] = useState({
    latest_score: 0, total_audits: 0,
    vulnerabilities: { high: 0, medium: 0, low: 0 },
    subscription_plan: 'free', score_history: []
  });
  const [recentAudits, setRecentAudits] = useState([]);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchDashboardData = useCallback(async (showSkeleton = true) => {
    if (!token) return;
    if (showSkeleton) setLoading(true);
    else setIsRefreshing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, auditsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/api/audits`, { headers })
      ]);
      if (statsRes.data) setStats(statsRes.data);
      if (auditsRes.data && Array.isArray(auditsRes.data)) {
        setRecentAudits(auditsRes.data.slice(0, 5));
        // Compte les audits du mois en cours
        const now = new Date();
        const thisMonth = auditsRes.data.filter(a => {
          const d = new Date(a.created_at);
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        });
        setMonthlyCount(thisMonth.length);
      }
    } catch (error) {
      console.error('❌ Dashboard Error:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [token]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData, location.key]);

  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e';
    if (score >= 50) return '#eab308';
    return '#ef4444';
  };

  const plan = user?.subscription_plan || 'free';
  const limit = PLAN_LIMITS[plan];
  // Plan gratuit : 1 audit offert par compte (à vie), pas par mois
  const usedCount = plan === 'free' ? (stats?.total_audits || 0) : monthlyCount;
  const remaining = limit !== null ? Math.max(0, limit - usedCount) : null;
  const usagePercent = limit !== null ? Math.min(100, (usedCount / limit) * 100) : 0;

  if (loading) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-zinc-500 font-mono text-xs animate-pulse uppercase tracking-widest">Loading_Secure_Session...</p>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-in fade-in duration-700">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white font-mono uppercase tracking-tighter">
              USER_TERMINAL: {user?.name?.split(' ')[0] || 'OPERATOR'}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-zinc-500 text-sm font-mono tracking-widest">
                STATUS: <span className="text-green-500">SYSTEM_ACTIVE</span>
              </p>
              <button onClick={() => fetchDashboardData(false)} className={`text-zinc-600 hover:text-white transition-colors ${isRefreshing ? 'animate-spin' : ''}`}>
                <RefreshCcw className="w-3 h-3" />
              </button>
            </div>
          </div>
          <Button onClick={() => navigate('/audits/new')} className="bg-blue-600 hover:bg-blue-700 font-bold px-6 rounded-none font-mono">
            <Plus className="w-4 h-4 mr-2" /> NEW_AUDIT
          </Button>
        </div>

        {/* ✅ Compteur d'audits restants */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-blue-500" />
              <span className="text-zinc-400 text-xs font-mono uppercase tracking-widest">{plan === 'free' ? 'Audit_Offert' : 'Audits_Ce_Mois'}</span>
            </div>
            <span className="text-xs font-mono text-zinc-500 uppercase">
              Plan : <span className={`font-bold ${plan === 'enterprise' ? 'text-green-400' : plan === 'pro' ? 'text-blue-400' : 'text-zinc-300'}`}>{plan}</span>
            </span>
          </div>

          {limit !== null ? (
            <>
              <div className="flex items-end gap-2 mb-3">
                <span className="text-3xl font-black font-mono text-white">{usedCount}</span>
                <span className="text-zinc-600 font-mono text-sm mb-1">/ {limit} {limit > 1 ? 'audits' : 'audit'}</span>
                <span className={`ml-auto text-sm font-black font-mono ${remaining === 0 ? 'text-red-500' : remaining <= 1 ? 'text-yellow-500' : 'text-green-500'}`}>
                  {remaining} restant{remaining !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="w-full bg-zinc-800 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all ${usagePercent >= 100 ? 'bg-red-500' : usagePercent >= 75 ? 'bg-yellow-500' : 'bg-blue-500'}`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
              {remaining === 0 && (
                <div className="mt-3 flex items-center justify-between">
                 <p className="text-red-400 text-[10px] font-mono uppercase">{plan === 'free' ? 'Audit gratuit utilisé — choisissez un plan pour continuer' : 'Limite atteinte — passez au plan supérieur'}</p>
                  <button onClick={() => navigate('/billing')} className="text-blue-400 text-[10px] font-mono uppercase underline hover:text-blue-300">
                    Voir les plans →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-3xl font-black font-mono text-green-400">{monthlyCount}</span>
              <span className="text-zinc-500 font-mono text-sm">audits — illimités ∞</span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-sm hover:border-zinc-700 transition-colors">
            <div className="flex items-center justify-between mb-3 text-zinc-500 text-[10px] font-mono uppercase tracking-widest">
              <span>Last_Global_Score</span>
              <ShieldCheck className="w-4 h-4 text-blue-500" />
            </div>
            <div className="text-3xl font-black font-mono" style={{ color: getScoreColor(stats?.latest_score) }}>
              {stats?.latest_score || 0}%
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-sm hover:border-zinc-700 transition-colors">
            <div className="flex items-center justify-between mb-3 text-zinc-500 text-[10px] font-mono uppercase tracking-widest">
              <span>Critical_Threats</span>
              <AlertTriangle className="w-4 h-4 text-red-500" />
            </div>
            <div className="text-3xl font-black text-white font-mono">{stats?.vulnerabilities?.high || 0}</div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-sm hover:border-zinc-700 transition-colors">
            <div className="flex items-center justify-between mb-3 text-zinc-500 text-[10px] font-mono uppercase tracking-widest">
              <span>Total_Scans</span>
              <TrendingUp className="w-4 h-4 text-green-500" />
            </div>
            <div className="text-3xl font-black text-white font-mono">{stats?.total_audits || 0}</div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-sm hover:border-zinc-700 transition-colors">
            <div className="flex items-center justify-between mb-3 text-zinc-500 text-[10px] font-mono uppercase tracking-widest">
              <span>Security_Tier</span>
              <FileText className="w-4 h-4 text-blue-500" />
            </div>
            <div className="text-2xl font-black text-white font-mono uppercase">{stats?.subscription_plan || 'Free'}</div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
          <h2 className="text-zinc-300 text-xs font-mono uppercase mb-6 tracking-widest flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-blue-500" /> Score_Telemetry_History
          </h2>
          {stats?.score_history?.length > 0 ? (
            <div className="h-[250px] w-full"><ScoreHistoryChart data={stats.score_history} /></div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-zinc-700 border border-dashed border-zinc-800 rounded bg-black/20">
              <p className="font-mono text-[10px] uppercase tracking-widest">Waiting for data injection...</p>
              <p className="text-[9px] mt-2 opacity-50">Execute your first scan to see analytics</p>
            </div>
          )}
        </div>

        {/* Recent Audits */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-800 flex justify-between items-center bg-black/20">
            <h2 className="text-white text-xs font-mono uppercase tracking-widest">Recent_Activity_Logs</h2>
            <Link to="/reports" className="text-blue-500 text-[10px] hover:text-blue-400 font-mono transition-colors underline uppercase tracking-tighter">View_All_Reports</Link>
          </div>
          <div className="divide-y divide-zinc-800">
            {recentAudits.length > 0 ? recentAudits.map((audit) => (
              <div key={audit?.audit_id} className="px-6 py-5 flex items-center justify-between hover:bg-white/[0.03] cursor-pointer group transition-all" onClick={() => navigate(`/audits/${audit.audit_id}`)}>
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-sm bg-black border ${audit?.security_score >= 80 ? 'border-green-500/20' : 'border-red-500/20'}`}>
                    <ShieldCheck className={`w-4 h-4 ${audit?.security_score >= 80 ? 'text-green-500' : 'text-red-500'}`} />
                  </div>
                  <div>
                    <div className="text-white text-sm font-bold font-mono group-hover:text-blue-400 transition-colors uppercase tracking-tight">{audit?.name || 'ANALYSIS_LOG'}</div>
                    <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-2 mt-1 uppercase">
                      <Globe className="w-3 h-3 opacity-50" /> {audit?.target_domain}
                      <span className="text-zinc-800">|</span>
                      <Clock className="w-3 h-3 opacity-50" /> {audit?.created_at ? new Date(audit.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-black font-mono" style={{ color: getScoreColor(audit?.security_score || 0) }}>{audit?.security_score ?? 0}%</div>
                    <div className="text-[8px] text-zinc-600 uppercase font-mono">Safety_Rating</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-800 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            )) : (
              <div className="px-6 py-16 text-center bg-black/10">
                <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.4em]">Database_Empty : No_Records_Found</p>
                <Button onClick={() => navigate('/audits/new')} variant="link" className="text-blue-600 font-mono text-xs mt-4 no-underline hover:underline">
                  {'>'} START_INITIAL_RECON
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
